import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Building, Users, Globe, TrendingUp, Shield, FileText, Plus, ExternalLink, FileSignature } from "lucide-react";
import { SharePointFileUpload } from "../ui/sharepoint-file-upload";
import DocumentListWithActions from "../documents/DocumentListWithActions";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "./PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

type UnderstandingEntityLandingProps = {
  onSelect: (section: string) => void;
};

const categories: Array<{
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}> = [
  {
    id: "external-factors",
    title: "External Factors",
    description: "Analyze external economic, industry, and regulatory factors affecting the entity.",
    icon: Globe,
    accent: "from-blue-500/20 to-indigo-500/20",
  },
  {
    id: "internal-factors",
    title: "Internal Factors",
    description: "Assess internal business operations, management, and organizational structure.",
    icon: Building,
    accent: "from-amber-500/20 to-orange-500/20",
  },
  {
    id: "accounting-policies",
    title: "Accounting Policies",
    description: "Review and understand the entity's accounting policies and procedures.",
    icon: FileText,
    accent: "from-emerald-500/20 to-teal-500/20",
  },
  {
    id: "other-considerations",
    title: "Other Considerations",
    description: "Evaluate additional factors and considerations relevant to the entity.",
    icon: TrendingUp,
    accent: "from-purple-500/20 to-pink-500/20",
  },
  {
    id: "understanding-internal-controls",
    title: "Understanding Internal Controls",
    description: "Assess the entity's internal control environment and framework.",
    icon: Shield,
    accent: "from-red-500/20 to-rose-500/20",
  },
];

const WORKSPACE_NAME = "Understanding the Entity & its Environment";

export default function UnderstandingEntityLanding({ onSelect }: UnderstandingEntityLandingProps) {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffsMap, setCardSignoffsMap] = useState<Record<string, MiscSignoffEntry[]>>({});
  const [docs, setDocs] = useState<Array<{ name: string; url: string; reference?: string; prep_so: any; rev_so: any; conc_so: any; eqr_so: any; partner_so: any }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const loadCardSignoffs = async () => {
    try {
      const data = await loadMiscSignoffs();
      const map: Record<string, MiscSignoffEntry[]> = {};
      categories.forEach((c) => {
        map[c.title] = getSignoffEntriesForCard(WORKSPACE_NAME, c.title, data.signoff);
      });
      setCardSignoffsMap(map);
    } catch {
      setCardSignoffsMap({});
    }
  };

  useEffect(() => {
    loadCardSignoffs();
  }, []);

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError("");
      // Use "Planning_UEE" to match the section field format used when linking files
      const sectionKey = "Planning_UEE";
      const res = await (window as any)?.sharePointAPI?.loadDocumentListBySection?.(sectionKey);
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load document list");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading document list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => { await loadDocs(); };
    run();
  }, []);

  const handleSubSectionClick = (subSectionId: string) => {
    // Navigate to the understanding-entity section with the specific sub-section
    onSelect(`understanding-entity-${subSectionId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Understanding the Entity & its Environment</h2>
          <p className="text-sm text-white/60">
            Choose a workflow to proceed. You can return here anytime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSignoffDialogOpen(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <FileSignature className="h-4 w-4 mr-1.5" />
            Sign off
          </Button>
          <Badge className="bg-white/10 text-white/80">Planning</Badge>
        </div>
      </div>

      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={categories.map((c) => c.title)}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((cat) => {
          const Icon = cat.icon;
          return (
            <Card
              key={cat.id}
              className="group cursor-pointer border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10"
              onClick={() => handleSubSectionClick(cat.id)}
            >
              <CardContent className="p-4">
                <div
                  className={`mb-4 inline-flex rounded-lg bg-gradient-to-br ${cat.accent} p-2`}
                >
                  <Icon className="h-5 w-5 text-white/80" />
                </div>
                <div className="mb-1 text-sm font-semibold">{cat.title}</div>
                <div className="mb-2 text-xs text-white/60">
                  {cat.description}
                </div>
                <PlanningSignoffTokens signoffs={cardSignoffsMap[cat.title]} />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 border-white/10 text-xs text-white/80 group-hover:bg-white/10 group-hover:text-white"
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Documents Section */}
      <DocumentListWithActions
        documents={docs}
        loading={loading}
        error={error}
        onRefresh={loadDocs}
        title="Document List"
        showUploadButton={true}
        uploadSectionList={["Planning_UEE_File"]}
        uploadFolderName="client"
        uploadMultiple={false}
        workspaceName="Understanding Entity"
        onNavigateToConfirmationLibrary={(w) => onSelect?.("confirmation-library:" + encodeURIComponent(w))}
        onNavigateToSamplingLibrary={(w) => onSelect?.("sampling-library:" + encodeURIComponent(w))}
      />
    </div>
  );
}
